import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import type { FaseStatus, PlaybookFaseDef, FaseEntry } from '@/lib/hooks/prestacoes';

interface TimelineItem {
  fase: number;
  nome: string;
  status: FaseStatus | 'PENDING';
  isCurrent: boolean;
  errorMessage?: string | null;
}

interface PrestTimelineProps {
  /** Catálogo das 16 fases (ordem e nomes) */
  fases: PlaybookFaseDef[];
  /** Log real de execução — opcional (ausente = read-only histórico, todas done) */
  faseLogs?: FaseEntry[];
  /** Fase em execução no momento */
  currentFase?: number | null;
  /** Modo all-done: ignora logs, mostra todos como DONE */
  allDone?: boolean;
}

function DotIcon({ status }: { status: TimelineItem['status'] }) {
  if (status === 'DONE') {
    return (
      <CheckCircle2
        size={13}
        strokeWidth={2.2}
        style={{ color: 'var(--pass)' }}
        aria-hidden
      />
    );
  }
  if (status === 'FAILED') {
    return (
      <AlertCircle
        size={13}
        strokeWidth={2.2}
        style={{ color: 'var(--block)' }}
        aria-hidden
      />
    );
  }
  if (status === 'STARTED') {
    return (
      <Loader2
        size={12}
        strokeWidth={2.2}
        style={{ color: 'var(--mint-deep)', animation: 'spin 1s linear infinite' }}
        aria-hidden
      />
    );
  }
  /* PENDING */
  return (
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: 'var(--card-stroke)',
        display: 'block',
      }}
      aria-hidden
    />
  );
}

/**
 * Timeline vertical reusável das 16 fases.
 * Usado em PrestacaoActiveModule (com logs ao vivo) e PrestDetailModule (all-done).
 */
export function PrestTimeline({ fases, faseLogs = [], currentFase, allDone }: PrestTimelineProps) {
  const items: TimelineItem[] = fases.map((def) => {
    if (allDone) {
      return { fase: def.fase, nome: def.nome, status: 'DONE', isCurrent: false };
    }
    const log = faseLogs.find((f) => f.fase === def.fase);
    const isCurrent = currentFase === def.fase;
    const status: FaseStatus | 'PENDING' = log?.status ?? 'PENDING';
    return {
      fase: def.fase,
      nome: def.nome,
      status,
      isCurrent,
      errorMessage: log?.errorMessage,
    };
  });

  return (
    <ol
      aria-label="Fases da prestação"
      style={{ display: 'flex', flexDirection: 'column', gap: 0, listStyle: 'none', padding: 0, margin: 0 }}
    >
      {items.map((item, idx) => {
        const isLast = idx === items.length - 1;
        return (
          <li
            key={item.fase}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              padding: '6px 0',
              background: item.isCurrent ? 'var(--mint-soft)' : 'transparent',
              borderRadius: item.isCurrent ? 'var(--r-2)' : 0,
              paddingLeft: item.isCurrent ? 6 : 0,
              minHeight: 44,
            }}
          >
            {/* dot + linha vertical */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flexShrink: 0,
                width: 20,
              }}
            >
              <div
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: item.status === 'DONE'
                    ? 'var(--pass-soft)'
                    : item.status === 'FAILED'
                    ? 'var(--block-soft, var(--warn-soft))'
                    : item.status === 'STARTED'
                    ? 'var(--mint-soft)'
                    : 'var(--card-soft)',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <DotIcon status={item.status} />
              </div>
              {!isLast && (
                <div
                  style={{
                    width: 1,
                    flex: 1,
                    minHeight: 12,
                    background: 'var(--card-stroke)',
                    marginTop: 2,
                  }}
                  aria-hidden
                />
              )}
            </div>

            {/* label */}
            <div style={{ flex: 1, minWidth: 0, paddingTop: 1 }}>
              <p
                style={{
                  fontSize: 13,
                  fontWeight: item.isCurrent ? 600 : 500,
                  color: item.status === 'FAILED' ? 'var(--block)' : 'var(--fg)',
                  margin: 0,
                  lineHeight: 1.3,
                }}
              >
                {item.fase}. {item.nome}
              </p>
              {item.errorMessage && (
                <p
                  style={{
                    fontSize: 10.5,
                    fontFamily: 'var(--font-mono)',
                    color: 'var(--warn)',
                    marginTop: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.errorMessage}
                </p>
              )}
              {item.isCurrent && (
                <p
                  style={{
                    fontSize: 10,
                    fontFamily: 'var(--font-mono)',
                    color: 'var(--mint-deep)',
                    marginTop: 2,
                  }}
                >
                  em progresso…
                </p>
              )}
            </div>

            {/* número da fase mono pequeno */}
            <span
              style={{
                fontSize: 10,
                fontFamily: 'var(--font-mono)',
                color: 'var(--fg-dim)',
                marginTop: 3,
                flexShrink: 0,
              }}
              aria-hidden
            >
              {String(item.fase).padStart(2, '0')}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
